/**
 * Anomalies Tab Component
 * List and manage anomalies for the project
 */

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Search, Filter, Eye, Trash2, X, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { type DBProject } from '../../../db';
import { projectsApi, photosApi } from '../../../lib/api';
import { useUIStore } from '../../../store/uiStore';

interface DBAnomaly {
  id: string;
  code: string;
  level: 1 | 2 | 3;
  description: string;
  note?: string;
  elementId?: string;
  elementType?: string;
  elementCode?: string;
  photoId: string;
  photoFileName?: string;
  geometry: any;
  createdAt: string;
}

interface AnomaliesTabProps {
  project: DBProject;
}

type SeverityFilter = 'all' | 1 | 2 | 3;

const severityColors: Record<1 | 2 | 3, { bg: string; text: string; border: string; label: string }> = {
  1: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', label: 'Low' },
  2: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Medium' },
  3: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', label: 'High' },
};

export function AnomaliesTab({ project }: AnomaliesTabProps) {
  const [anomalies, setAnomalies] = useState<DBAnomaly[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [viewingAnomaly, setViewingAnomaly] = useState<DBAnomaly | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  
  const navigate = useNavigate();
  const { setCurrentPhoto, selectElement } = useUIStore();

  useEffect(() => {
    loadAnomalies();
  }, [project.id]);

  // Load photo when viewing anomaly
  useEffect(() => {
    if (viewingAnomaly?.photoId) {
      loadPhotoForAnomaly(viewingAnomaly.photoId);
    } else {
      setPhotoUrl(null);
    }
  }, [viewingAnomaly]);

  const loadPhotoForAnomaly = async (photoId: string) => {
    try {
      setLoadingPhoto(true);
      const response = await photosApi.getUrl(photoId, true); // true = thumbnail
      setPhotoUrl(response.data?.data?.url || response.data?.url);
    } catch (error) {
      console.error('Failed to load photo URL:', error);
      setPhotoUrl(null);
    } finally {
      setLoadingPhoto(false);
    }
  };

  const loadAnomalies = async () => {
    try {
      const response = await projectsApi.getAnomalies(project.id, {
        page: 1,
        limit: 1000, // Load all anomalies
      });
      
      const anomaliesData = response.data?.data || [];
      
      // Transform API response to match expected structure
      const transformedAnomalies: DBAnomaly[] = anomaliesData.map((a: any) => ({
        id: a.id,
        code: a.code,
        level: a.level,
        description: a.description || '',
        note: a.note,
        elementId: a.elementId,
        elementType: a.element?.type || '',
        elementCode: a.element?.code || '',
        photoId: a.photoId,
        photoFileName: a.photo?.filename || '',
        geometry: a.geometry,
        createdAt: a.createdAt,
      }));
      
      setAnomalies(transformedAnomalies);
    } catch (error) {
      console.error('Failed to load anomalies:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter anomalies
  const filteredAnomalies = useMemo(() => {
    return anomalies.filter(anomaly => {
      // Severity filter
      if (severityFilter !== 'all' && anomaly.level !== severityFilter) {
        return false;
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          anomaly.code.toLowerCase().includes(query) ||
          anomaly.description.toLowerCase().includes(query) ||
          anomaly.elementCode.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [anomalies, severityFilter, searchQuery]);

  // Stats by severity
  const stats = useMemo(() => {
    return {
      total: anomalies.length,
      level1: anomalies.filter(a => a.level === 1).length,
      level2: anomalies.filter(a => a.level === 2).length,
      level3: anomalies.filter(a => a.level === 3).length,
    };
  }, [anomalies]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (anomalies.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-900 p-8">
        <AlertTriangle className="w-16 h-16 text-slate-400 dark:text-slate-600 mb-4" />
        <h3 className="text-lg font-mono font-medium text-slate-700 dark:text-slate-200 mb-2">
          No anomalies
        </h3>
        <p className="text-slate-500 text-center max-w-md font-mono text-sm">
          No anomalies have been recorded for this project.
          Anomalies will be displayed here when added.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-100 dark:bg-slate-900">
      {/* Stats bar */}
      <div className="bg-white/90 dark:bg-slate-800/90 border-b border-slate-200 dark:border-slate-700/50 px-6 py-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
              {stats.total} anomalies
            </span>
          </div>
          
          <div className="flex items-center gap-4 font-mono text-sm">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-slate-500 dark:text-slate-400">
                High: <span className="font-medium text-slate-700 dark:text-slate-200">{stats.level3}</span>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-slate-500 dark:text-slate-400">
                Medium: <span className="font-medium text-slate-700 dark:text-slate-200">{stats.level2}</span>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-slate-500 dark:text-slate-400">
                Low: <span className="font-medium text-slate-700 dark:text-slate-200">{stats.level1}</span>
              </span>
            </div>
          </div>

          <div className="flex-1" />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700/50 px-6 py-3 flex items-center gap-4">
        {/* Search */}
        <div className="flex-1 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
          <input
            type="text"
            placeholder="Search by code, description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-mono text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
          />
        </div>

        {/* Severity filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400 dark:text-slate-500" />
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value) as 1 | 2 | 3)}
            className="px-3 py-2 bg-white dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-mono text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500/50"
          >
            <option value="all">All levels</option>
            <option value="3">Level 3 - High</option>
            <option value="2">Level 2 - Medium</option>
            <option value="1">Level 1 - Low</option>
          </select>
        </div>
      </div>

      {/* Anomalies list */}
      <div className="flex-1 overflow-auto p-6 dark-scrollbar">
        {filteredAnomalies.length === 0 ? (
          <div className="text-center py-12 text-slate-500 font-mono">
            No anomalies found with current filters
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-mono font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-mono font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-mono font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-mono font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Element</th>
                  <th className="px-4 py-3 text-center text-xs font-mono font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Level</th>
                  <th className="px-4 py-3 text-left text-xs font-mono font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Photo</th>
                  <th className="px-4 py-3 text-right text-xs font-mono font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700/50">
                {filteredAnomalies.map((anomaly, index) => {
                  const severity = severityColors[anomaly.level];
                  return (
                    <tr key={anomaly.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                      <td className="px-4 py-3 text-sm font-mono text-slate-500">
                        #{index + 1}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium text-slate-700 dark:text-slate-200">
                          {anomaly.code}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                          {anomaly.description}
                        </p>
                        {anomaly.note && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1 font-mono">
                            Note: {anomaly.note}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono text-slate-500 dark:text-slate-400">
                          {anomaly.elementType} #{anomaly.elementCode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-medium border ${severity.bg} ${severity.text} ${severity.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${anomaly.level === 3 ? 'bg-red-500' : anomaly.level === 2 ? 'bg-amber-500' : 'bg-green-500'}`} />
                          LVL {anomaly.level}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-slate-500 truncate max-w-[120px] block">
                          {anomaly.photoFileName}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button 
                            onClick={() => setViewingAnomaly(anomaly)}
                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                          </button>
                          <button 
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this anomaly?')) {
                                // TODO: Delete anomaly
                                console.log('Delete anomaly:', anomaly.id);
                              }
                            }}
                            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick view modal */}
      {viewingAnomaly && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800/95 backdrop-blur-sm rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200 dark:border-slate-700/50">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400" />
                <div>
                  <h3 className="font-mono font-semibold text-slate-800 dark:text-slate-100">
                    {viewingAnomaly.code} - {viewingAnomaly.description}
                  </h3>
                  <p className="text-sm font-mono text-slate-500">
                    {viewingAnomaly.elementType} #{viewingAnomaly.elementCode}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewingAnomaly(null)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 dark-scrollbar bg-slate-50 dark:bg-transparent">
              <div className="grid grid-cols-1 gap-6">
                {/* Details row */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Severity Level</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded text-sm font-mono font-medium border ${severityColors[viewingAnomaly.level].bg} ${severityColors[viewingAnomaly.level].text} ${severityColors[viewingAnomaly.level].border}`}>
                        <span className={`w-2 h-2 rounded-full ${viewingAnomaly.level === 3 ? 'bg-red-500' : viewingAnomaly.level === 2 ? 'bg-amber-500' : 'bg-green-500'}`} />
                        Level {viewingAnomaly.level} - {severityColors[viewingAnomaly.level].label}
                      </span>
                    </div>
                  </div>

                  {viewingAnomaly.note && (
                    <div>
                      <label className="text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Note</label>
                      <p className="mt-1 text-sm text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/50 rounded-lg p-3 font-mono">
                        {viewingAnomaly.note}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Photo</label>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300 font-mono">
                      {viewingAnomaly.photoFileName}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Created At</label>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300 font-mono">
                      {new Date(viewingAnomaly.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Photo preview - FULL SIZE */}
                <div className="col-span-2">
                  <label className="text-xs font-mono font-medium text-slate-500 uppercase tracking-wider mb-2 block">Photo with Anomaly Marker</label>
                  <div className="bg-slate-200 dark:bg-slate-900/50 rounded-lg h-[500px] flex items-center justify-center overflow-hidden relative border border-slate-300 dark:border-slate-700/50">
                    {loadingPhoto ? (
                      <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
                    ) : photoUrl ? (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <img
                          src={photoUrl}
                          alt="Anomaly photo"
                          className="max-w-full max-h-full object-contain"
                          onLoad={(e) => {
                            const img = e.currentTarget;
                            img.setAttribute('data-width', img.naturalWidth.toString());
                            img.setAttribute('data-height', img.naturalHeight.toString());
                          }}
                        />
                        {/* Anomaly marker overlay */}
                        {viewingAnomaly.geometry && (
                          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                            {(() => {
                              const geom = viewingAnomaly.geometry as any;
                              if (geom.type === 'point' && geom.coordinates) {
                                const [x, y] = geom.coordinates;
                                return (
                                  <div
                                    className="absolute w-12 h-12 border-4 border-red-500 rounded-full bg-red-500/30 shadow-lg animate-pulse"
                                    style={{
                                      left: `${x}%`,
                                      top: `${y}%`,
                                      transform: 'translate(-50%, -50%)',
                                    }}
                                  >
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className="w-2 h-2 bg-red-600 rounded-full" />
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <Camera className="w-12 h-12 text-slate-400 dark:text-slate-600" />
                        <p className="text-sm font-mono text-slate-500 ml-2">No preview available</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700/50 flex justify-end gap-3 bg-white dark:bg-transparent">
              <button
                onClick={() => setViewingAnomaly(null)}
                className="px-4 py-2 font-mono text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                onClick={async () => {
                  console.log('🔵 BUTTON CLICKED! viewingAnomaly:', viewingAnomaly);
                  
                  if (!viewingAnomaly?.photoId) {
                    console.error('❌ Missing photoId');
                    toast.error('Missing photo information');
                    return;
                  }
                  
                  if (!viewingAnomaly?.elementId) {
                    console.warn('⚠️ Missing elementId, will try without it');
                  }
                  
                  try {
                    console.log('🔄 Step 1: Opening photo in viewer...', {
                      photoId: viewingAnomaly.photoId,
                      elementId: viewingAnomaly.elementId,
                      elementType: viewingAnomaly.elementType
                    });
                    
                    // 1. Select element if available
                    if (viewingAnomaly.elementId && viewingAnomaly.elementType) {
                      console.log('🔄 Step 2: Selecting element...');
                      const elementType = viewingAnomaly.elementType.toUpperCase();
                      selectElement(viewingAnomaly.elementId, elementType as any);
                      console.log('✅ Element selected');
                    }
                    
                    // 2. Load full-size photo URL
                    console.log('🔄 Step 3: Loading photo URL...');
                    const response = await photosApi.getUrl(viewingAnomaly.photoId, false);
                    console.log('📦 Photo URL response:', response.data);
                    const fullPhotoUrl = response.data?.data?.url || response.data?.url;
                    
                    if (!fullPhotoUrl) {
                      throw new Error('Failed to get photo URL from response');
                    }
                    console.log('✅ Photo URL:', fullPhotoUrl.substring(0, 100));
                    
                    // 3. Fetch blob and create File
                    console.log('🔄 Step 4: Fetching blob...');
                    const photoResponse = await fetch(fullPhotoUrl);
                    const blob = await photoResponse.blob();
                    console.log('✅ Blob fetched:', blob.size, 'bytes');
                    
                    const file = new File([blob], viewingAnomaly.photoFileName || 'photo.jpg', { type: blob.type });
                    console.log('✅ File created:', file.name);
                    
                    // 4. Set current photo (this opens PhotoViewer)
                    console.log('🔄 Step 5: Setting current photo...');
                    setCurrentPhoto({
                      id: viewingAnomaly.photoId,
                      projectId: project.id, // Pass projectId for loading anomalies!
                      file,
                      dataUrl: fullPhotoUrl,
                      width: 0,
                      height: 0,
                    });
                    console.log('✅ Current photo set!');
                    
                    // 5. Switch to Map tab FIRST (before closing modal)
                    console.log('🔄 Step 6: Navigating to Map tab...');
                    navigate(`/projects/${project.id}/map`);
                    console.log('✅ Navigated to Map tab');
                    
                    // 6. Close modal after a short delay
                    console.log('🔄 Step 7: Closing modal...');
                    setTimeout(() => {
                      setViewingAnomaly(null);
                      console.log('✅ Modal closed');
                    }, 100);
                    
                    console.log('✅✅✅ ALL DONE! PhotoViewer should be visible now!');
                  } catch (error) {
                    console.error('❌❌❌ FAILED:', error);
                    toast.error('Failed to open photo', {
                      description: error instanceof Error ? error.message : 'Unknown error',
                    });
                  }
                }}
                className="px-4 py-2 bg-blue-600/80 text-white rounded-lg hover:bg-blue-600 transition-colors font-mono border border-blue-500/50"
              >
                Open in Viewer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
